import {
  getPatientNameWithStatus,
  sitesQueryOptions,
  useParentRoutesPatient,
  usePatient,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { Outlet, useParams } from "react-router";
import { z } from "zod";
import styles from "./Patient.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { Text } from "@medoma/components/Text/Text";
import * as Sentry from "@sentry/react";
import { useLingui } from "@lingui/react/macro";
import { CallPatientOrRelatives } from "@/pages/ambulating/patients/$patientId/CallPatientOrRelatives";
import PinIcon from "@medoma/components/icons/PinIcon";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { ProspectActions } from "./ProspectActions";
import { PatientSectionLinks } from "./PatientSectionLinks";
import { Link } from "@medoma/components/Link/Link";
import { useQuery } from "@tanstack/react-query";

export const Patient = () => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();

  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { data: patient, isPending, isError, error } = usePatient(patientId);

  const { data: sites } = useQuery(sitesQueryOptions);

  if (isPending) {
    return <Loading message={t`Laddar patient`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patientdetaljer.`} ${deducedError(error)}`}
      />
    );
  }

  const displaySiteName = sites && sites.length > 1;

  return (
    <>
      <div className={styles.pageHeading}>
        <div className={styles.pageTitle}>
          <Heading level="h1">
            {getPatientNameWithStatus(patient, "full")}
          </Heading>
          <Text element="div" size="large">
            {formatPersonalIdentityNumber({
              personalIdentityNumber: patient.personalIdentityNumber,
              personalIdentityNumberType: patient.personalIdentityNumberType,
            })}
          </Text>
          {displaySiteName ? (
            <Text element="span" color="faded" size="large">
              {patient.site.displayName}
            </Text>
          ) : null}
        </div>
        <div className={styles.iconContainer}>
          <Link
            aria-label={t`Öppna adress i Google Maps`}
            className={styles.pinLink}
            type="external"
            to={`https://www.google.com/maps/dir/?api=1&destination=${patient.address.coordinates.latitude},${patient.address.coordinates.longitude}&travelmode=driving`}
          >
            <PinIcon />
          </Link>
          <CallPatientOrRelatives
            patient={{
              ...patient,
              name: getPatientFullName(patient.name),
              phoneNumber: patient.phoneNumber
                ? localizePhoneNumber({
                    countryCode,
                    phoneNumber: patient.phoneNumber,
                  })
                : undefined,
            }}
            relatives={patient.relatives
              .filter(
                ({ phoneNumber }) =>
                  phoneNumber !== null && phoneNumber !== undefined,
              )
              .map(({ name, relation, phoneNumber }) => ({
                name: `${name} (${relation})`,
                phoneNumber: localizePhoneNumber({
                  countryCode,
                  phoneNumber: phoneNumber!,
                }),
              }))}
          />
        </div>
      </div>
      <Outlet context={patient} />
    </>
  );
};

export const PatientNavigation = () => {
  const patient = useParentRoutesPatient();

  return (
    <div className={styles.patientNavigation}>
      {patient.status === patientStatusSchema.enum.prospect ? (
        <ProspectActions />
      ) : null}
      <PatientSectionLinks patientId={patient.id} />
    </div>
  );
};
